import TRTC from 'trtc-sdk-v5';
import { t } from './i18n';
import { buildCaptureOptions, isDesktopRuntime, isRetryableDisplayMediaError, openDesktopTabMode, type CaptureMode, type CaptureOptions } from './capture';
export { isDesktopRuntime, openDesktopTabMode } from './capture';
export type { CaptureMode } from './capture';

export type TrtcClient = ReturnType<typeof TRTC.create>;

export type SystemAudioCapture = {
  stream: MediaStream;
  track: MediaStreamTrack;
  stop: () => void;
};

export type HostRoomOptions = {
  sdkAppId: number;
  roomId: string;
  userId: string;
  userSig: string;
  privateMapKey: string;
  audioTrack: MediaStreamTrack;
  onConnectionState?: (state: 'connected' | 'connecting' | 'disconnected') => void;
  onNetworkQuality?: (latencyMs: number) => void;
};

export type ListenerRoomOptions = {
  sdkAppId: number;
  roomId: string;
  userId: string;
  ownerUserId: string;
  userSig: string;
  privateMapKey: string;
  volume: number;
  onConnectionState?: (state: 'connected' | 'connecting' | 'disconnected') => void;
  onNetworkQuality?: (latencyMs: number) => void;
  onCommand?: (command: { type: 'pause' | 'resume'; targetUserId?: string }) => void;
  onRemoteAudio?: (available: boolean) => void;
  onError?: (error: unknown) => void;
  onKickedOut?: (reason: string) => void;
};

export function isSystemAudioCaptureSupported(): boolean {
  return isDesktopRuntime() || (Boolean(navigator.mediaDevices?.getDisplayMedia) && /Chrome|Edg\//.test(navigator.userAgent));
}

async function requestDisplayStream(mode: CaptureMode): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getDisplayMedia(buildCaptureOptions(mode));
  } catch (error) {
    if (!isRetryableDisplayMediaError(error)) throw error;
    const fallbackOptions: CaptureOptions = { video: true, audio: true, selfBrowserSurface: 'exclude' };
    return navigator.mediaDevices.getDisplayMedia(fallbackOptions);
  }
}

export async function captureSharedAudio(mode: CaptureMode): Promise<SystemAudioCapture> {
  const stream = await requestDisplayStream(mode);
  const track = stream.getAudioTracks()[0];
  const videoTrack = stream.getVideoTracks()[0];
  const stopTracks = () => stream.getTracks().forEach((item) => item.stop());

  if (!track) {
    stopTracks();
    throw new Error(mode === 'system'
      ? t('没有获取到系统声音。请在共享选择器中选择“整个屏幕”，并勾选“分享系统音频”。')
      : t('没有获取到标签页音频。请选择正在播放声音的 Chrome/Edge 标签页，并勾选“分享标签页音频”。'));
  }

  const displaySurface = videoTrack?.getSettings().displaySurface;
  if (mode === 'system' && displaySurface && displaySurface !== 'monitor') {
    stopTracks();
    throw new Error(t('电脑系统音频模式必须选择“整个屏幕”，不能选择标签页或窗口。'));
  }
  if (mode === 'tab' && displaySurface && displaySurface !== 'browser') {
    stopTracks();
    throw new Error(t('Chrome 标签页模式必须选择一个浏览器标签页，不能选择整个屏幕。'));
  }

  return {
    stream,
    track,
    stop: stopTracks
  };
}

export async function enterHostRoom(options: HostRoomOptions): Promise<{
  client: TrtcClient;
  stop: () => Promise<void>;
  sendTargetCommand: (targetUserId: string, type: 'pause' | 'resume') => void;
}> {
  const client = TRTC.create();
  client.on(TRTC.EVENT.CONNECTION_STATE_CHANGED, (event: any) => {
    const state = String(event?.state ?? '').toLowerCase();
    if (state.includes('connected')) options.onConnectionState?.('connected');
    else if (state.includes('connecting') || state.includes('reconnecting')) options.onConnectionState?.('connecting');
    else options.onConnectionState?.('disconnected');
  });
  client.on(TRTC.EVENT.NETWORK_QUALITY, (event: any) => {
    const latency = Number(event?.uplinkRTT ?? event?.rtt ?? 0);
    if (Number.isFinite(latency)) options.onNetworkQuality?.(Math.max(0, Math.round(latency)));
  });

  await client.enterRoom({
    sdkAppId: options.sdkAppId,
    userId: options.userId,
    userSig: options.userSig,
    privateMapKey: options.privateMapKey,
    strRoomId: options.roomId,
    scene: TRTC.TYPE.SCENE_LIVE,
    role: TRTC.TYPE.ROLE_ANCHOR,
    autoReceiveAudio: false,
    autoReceiveVideo: false
  });
  let audioStarted = false;
  try {
    await client.startLocalAudio({
      publish: true,
      option: {
        audioTrack: options.audioTrack,
        profile: TRTC.TYPE.AUDIO_PROFILE_HIGH_STEREO,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        captureVolume: 100
      }
    });
    audioStarted = true;

  } catch (error) {
    if (audioStarted) await client.stopLocalAudio().catch(() => undefined);
    await client.exitRoom().catch(() => undefined);
    client.destroy();
    throw error;
  }

  return {
    client,
    sendTargetCommand: (targetUserId, type) => {
      client.sendCustomMessage({
        cmdId: type === 'pause' ? 2 : 3,
        data: new TextEncoder().encode(JSON.stringify({
          type,
          targetUserId,
          issuedAt: new Date().toISOString()
        })).buffer
      });
    },
    stop: async () => {
      if (audioStarted) await client.stopLocalAudio().catch(() => undefined);
      await client.exitRoom().catch(() => undefined);
      client.destroy();
    }
  };
}

export async function enterListenerRoom(options: ListenerRoomOptions): Promise<{
  client: TrtcClient;
  setVolume: (volume: number) => Promise<void>;
  setPaused: (paused: boolean) => Promise<void>;
  stop: () => Promise<void>;
}> {
  const client = TRTC.create();
  let audioAvailable = false;
  let paused = false;

  const applyVolume = async (volume: number) => {
    await client.setRemoteAudioVolume(options.ownerUserId, volume);
  };

  const setPaused = async (nextPaused: boolean) => {
    paused = nextPaused;
    await client.muteRemoteAudio(options.ownerUserId, nextPaused);
  };

  client.on(TRTC.EVENT.CONNECTION_STATE_CHANGED, (event: any) => {
    const state = String(event?.state ?? '').toLowerCase();
    if (state.includes('connected')) options.onConnectionState?.('connected');
    else if (state.includes('connecting') || state.includes('reconnecting')) options.onConnectionState?.('connecting');
    else options.onConnectionState?.('disconnected');
  });
  client.on(TRTC.EVENT.NETWORK_QUALITY, (event: any) => {
    const latency = Number(event?.downlinkRTT ?? event?.rtt ?? 0);
    if (Number.isFinite(latency)) options.onNetworkQuality?.(Math.max(0, Math.round(latency)));
  });
  client.on(TRTC.EVENT.REMOTE_AUDIO_AVAILABLE, async () => {
    audioAvailable = true;
    await applyVolume(options.volume).catch(() => undefined);
    options.onRemoteAudio?.(true);
  });
  client.on(TRTC.EVENT.REMOTE_AUDIO_UNAVAILABLE, () => {
    audioAvailable = false;
    options.onRemoteAudio?.(false);
  });
  client.on(TRTC.EVENT.ERROR, (error: unknown) => {
    options.onError?.(error);
  });
  client.on(TRTC.EVENT.KICKED_OUT, (event: any) => {
    options.onKickedOut?.(String(event?.reason ?? 'kicked'));
  });
  client.on(TRTC.EVENT.CUSTOM_MESSAGE, (event: any) => {
    try {
      const text = new TextDecoder().decode(event.data as ArrayBuffer);
      options.onCommand?.(JSON.parse(text));
    } catch {
      // Ignore malformed control messages.
    }
  });

  await client.enterRoom({
    sdkAppId: options.sdkAppId,
    userId: options.userId,
    userSig: options.userSig,
    privateMapKey: options.privateMapKey,
    strRoomId: options.roomId,
    scene: TRTC.TYPE.SCENE_LIVE,
    role: TRTC.TYPE.ROLE_AUDIENCE,
    autoReceiveAudio: true,
    autoReceiveVideo: false,
    enableAutoPlayDialog: true
  });
  if (audioAvailable) await applyVolume(options.volume).catch(() => undefined);

  return {
    client,
    setVolume: async (volume) => {
      await applyVolume(volume);
    },
    setPaused,
    stop: async () => {
      await client.exitRoom().catch(() => undefined);
      client.destroy();
    }
  };
}

export async function getSpeakerList(): Promise<MediaDeviceInfo[]> {
  try {
    return await TRTC.getSpeakerList(false);
  } catch {
    return [];
  }
}

export async function setSpeaker(speakerId: string): Promise<void> {
  await TRTC.setCurrentSpeaker(speakerId);
}
